import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Check, Loader2 } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';

// `available: false` channels aren't wired to a connect flow yet — they show a
// muted "Soon" pill instead of an actionable Connect button, so a CMO doesn't
// click into a dead end.
const SUPPORTED_CHANNELS = [
  // Google Ads connect is Tier-1 on api-v2 Ad Accounts (not cowork user_integrations).
  { provider: 'google-ads',  label: 'Google Ads',         category: 'Paid',     icon: '🔵', available: true, useAdAccounts: true },
  { provider: 'meta-ads',    label: 'Meta Ads',           category: 'Paid',     icon: '🔷' },
  { provider: 'tiktok-ads',  label: 'TikTok Ads',         category: 'Paid',     icon: '🎵', available: false },
  { provider: 'linkedin',    label: 'LinkedIn',           category: 'Social',   icon: '💼', available: true },
  { provider: 'instagram',   label: 'Instagram',          category: 'Social',   icon: '📸', available: false },
  { provider: 'twitter',     label: 'X / Twitter',        category: 'Social',   icon: '🐦', available: false },
  { provider: 'tiktok',      label: 'TikTok',             category: 'Social',   icon: '🎬', available: false },
  { provider: 'ga4',         label: 'Google Analytics 4', category: 'Analytics', icon: '📊', available: true },
  { provider: 'klaviyo',     label: 'Klaviyo',            category: 'Email/CRM', icon: '✉️', available: false },
  { provider: 'hubspot',     label: 'HubSpot',            category: 'Email/CRM', icon: '🟠', available: false },
  { provider: 'gmail',       label: 'Gmail',              category: 'Email/CRM', icon: '✉️', available: false },
  { provider: 'slack',       label: 'Slack',              category: 'Comms',     icon: '💬', available: true },
];

interface Integration {
  id: string;
  provider: string;
  profileName?: string | null;
  profileImage?: string | null;
  createdAt?: string;
}

interface IntegrationsPageProps {
  onOpenGoogleAds: () => void;
}

/**
 * Tier-1 paid ads (Meta, Google, …) use the team-scoped ad-account flow on
 * api-v2. Keeping this link separate from the legacy Cowork integrations API
 * prevents a browser session token from being sent through an OAuth URL.
 */
function CanonicalAdAccountsCard({
  testId,
  icon,
  title,
  description,
  manageLabel,
  manageAria,
}: {
  testId: string;
  icon: string;
  title: string;
  description: string;
  manageLabel: string;
  manageAria: string;
}) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border border-[#eaecf0] hover:border-[#be43ff]/30 transition-colors"
      data-testid={testId}
    >
      <div className="text-[20px] w-8 h-8 flex items-center justify-center bg-[#f8fafc] rounded-md" aria-hidden>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[#101828]">{title}</div>
        <div className="text-[11px] text-[#94a3b8]">{description}</div>
      </div>
      <Link
        to="/ad-accounts"
        className="inline-flex items-center gap-1 text-[11px] text-[#be43ff] px-2.5 py-1 rounded-md hover:bg-[#f8f5ff] transition-colors"
        style={{ fontWeight: 600 }}
        aria-label={manageAria}
      >
        {manageLabel}
        <ExternalLink className="w-3 h-3" aria-hidden />
      </Link>
    </div>
  );
}

/**
 * Cowork channel list. Tier-1 paid ads (Meta, Google) deep-link to /ad-accounts
 * (api-v2); other providers still use cowork user_integrations OAuth.
 *
 * Side effects: lists/deletes cowork integrations; navigates on retired OAuth
 * callbacks (`meta_*`, `gads_*`) and Connect for Google Ads.
 */
export function IntegrationsPage({ onOpenGoogleAds }: IntegrationsPageProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  // Prop kept for CoworkPage callers; Google Ads no longer opens the legacy agent view.
  void onOpenGoogleAds;

  const refresh = useCallback(() => {
    setLoading(true);
    coworkApi.integrations.list()
      .then(d => setItems(d.integrations || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-check after returning from any OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Former Cowork Meta / Google Ads OAuth callbacks are retired — send them
    // to the team-scoped Ad Accounts flow instead of loading legacy state.
    if (
      params.has('meta_connected') || params.has('meta_error')
      || params.get('gads_connected') === 'true' || params.has('gads_error')
    ) {
      navigate('/ad-accounts', { replace: true });
      return;
    }

    const consumed: string[] = [];

    if (params.get('integration_success') || params.get('slack_connected') === 'true') {
      refresh();
      ['integration_success', 'slack_connected', 'brandId', 'profile'].forEach(k => consumed.push(k));
    }
    if (params.get('integration_error') || params.get('slack_error')) {
      alert(`Integration failed: ${params.get('integration_error') || params.get('slack_error')}`);
      ['integration_error', 'slack_error'].forEach(k => consumed.push(k));
    }

    if (consumed.length) {
      consumed.forEach(k => params.delete(k));
      navigate(
        {
          pathname: location.pathname,
          search: params.size ? `?${params.toString()}` : '',
        },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate, refresh]);

  const handleConnect = (provider: string) => {
    // Google Ads connect lives on api-v2 Ad Accounts, not cowork OAuth.
    if (provider === 'google-ads') {
      navigate('/ad-accounts');
      return;
    } else if (provider === 'linkedin') {
      window.location.href = coworkApi.integrations.connectUrl();
    } else if (provider === 'slack') {
      window.location.href = coworkApi.slack.connectUrl();
    } else if (provider === 'ga4') {
      window.location.href = coworkApi.ga4.connectUrl();
    } else {
      alert(`Connection flow for ${provider} coming soon.`);
    }
  };

  const handleDisconnect = async (integration: Integration) => {
    if (!confirm(`Disconnect ${integration.profileName || integration.provider}? Your agents will lose access to this account.`)) return;
    setDisconnecting(integration.id);
    try {
      await coworkApi.integrations.delete(integration.id);
      setItems(prev => prev.filter(i => i.id !== integration.id));
    } catch (err) {
      alert(`Failed to disconnect: ${(err as Error).message}`);
    } finally {
      setDisconnecting(null);
    }
  };

  const grouped = SUPPORTED_CHANNELS.reduce<Record<string, typeof SUPPORTED_CHANNELS>>((acc, c) => {
    (acc[c.category] ||= []).push(c);
    return acc;
  }, {});

  const byProvider = items.reduce<Record<string, Integration>>((acc, i) => {
    acc[i.provider] = i;
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-6 py-4 border-b border-[#eaecf0]">
        <h1 className="text-[18px] font-semibold text-[#101828]">Channels & integrations</h1>
        <p className="text-[12px] text-[#667085] mt-0.5">Connect the platforms your agents work across.</p>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-[#94a3b8]" /></div>
        ) : (
          <div className="flex flex-col gap-6 max-w-4xl">
            {Object.entries(grouped).map(([category, channels]) => (
              <div key={category}>
                <div className="text-[11px] uppercase tracking-wider text-[#94a3b8] mb-2 font-semibold">{category}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {channels.map((c) => {
                    if (c.provider === 'meta-ads') {
                      return (
                        <CanonicalAdAccountsCard
                          key={c.provider}
                          testId="canonical-meta-ad-accounts"
                          icon="🔷"
                          title={t('adAccountsPage.canonicalMeta.title')}
                          description={t('adAccountsPage.canonicalMeta.description')}
                          manageLabel={t('adAccountsPage.canonicalMeta.manage')}
                          manageAria={t('adAccountsPage.canonicalMeta.manageAria')}
                        />
                      );
                    }
                    if ('useAdAccounts' in c && c.useAdAccounts) {
                      return (
                        <CanonicalAdAccountsCard
                          key={c.provider}
                          testId="canonical-google-ad-accounts"
                          icon={c.icon}
                          title={t('adAccountsPage.canonicalGoogle.title')}
                          description={t('adAccountsPage.canonicalGoogle.description')}
                          manageLabel={t('adAccountsPage.canonicalGoogle.manage')}
                          manageAria={t('adAccountsPage.canonicalGoogle.manageAria')}
                        />
                      );
                    }

                    const integration = byProvider[c.provider];
                    const isConnected = !!integration;

                    let subtitle: string;
                    if (isConnected) {
                      subtitle = integration.profileName || 'Connected';
                    } else {
                      subtitle = 'Not connected';
                    }

                    return (
                      <div key={c.provider} className="flex items-center gap-3 p-3 rounded-lg border border-[#eaecf0] hover:border-[#be43ff]/30 transition-colors">
                        {integration?.profileImage ? (
                          <img src={integration.profileImage} alt="" className="w-8 h-8 rounded-md object-cover" />
                        ) : (
                          <div className="text-[20px] w-8 h-8 flex items-center justify-center bg-[#f8fafc] rounded-md">{c.icon}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#101828]">{c.label}</div>
                          <div className="text-[11px] text-[#94a3b8] truncate">{subtitle}</div>
                        </div>

                        {isConnected ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#16a34a] px-2.5 py-1 rounded-md bg-[#f0fdf4]">
                            <Check className="w-3 h-3" />
                            Connected
                          </span>
                        ) : c.available ? (
                          <button
                            onClick={() => handleConnect(c.provider)}
                            className="text-[11px] text-[#be43ff] px-2.5 py-1 rounded-md hover:bg-[#f8f5ff] transition-colors"
                            style={{ fontWeight: 600 }}
                          >
                            Connect
                          </button>
                        ) : (
                          <span className="text-[11px] text-[#94a3b8] px-2.5 py-1 rounded-md bg-[#f8fafc]" style={{ fontWeight: 500 }}>
                            Soon
                          </span>
                        )}

                        {isConnected && integration && (
                          <button
                            onClick={() => handleDisconnect(integration)}
                            disabled={disconnecting === integration.id}
                            className="text-[11px] text-[#94a3b8] hover:text-[#dc2626] px-2 py-1 rounded-md hover:bg-[#fef2f2] transition-colors disabled:opacity-50"
                            title="Disconnect"
                          >
                            {disconnecting === integration.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Disconnect'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
